import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { CAMPO } from "./ui";
import { Segmentos } from "./FerramentaUi";
import { formatarData } from "../lib/perito/datas";
import type { Tom } from "../lib/perito/format";
import { ETAPA_LABEL, TOTAL_ETAPAS, etapaNumDe } from "../lib/perito/pipeline";
import { prazoRelacionado, rotuloDias, tomDosDias } from "../lib/perito/proximaAcao";
import { usePerito } from "../lib/perito/store";
import type { Alerta } from "../lib/perito/alertas";
import type { NivelAlerta, Pericia } from "../lib/perito/tipos";

// As peças da Central do Perito (000385). Lá elas eram o `marca.tsx` (Chip, Rotulo, Alternador) e os
// componentes do shadcn; aqui são elementos nativos com os tokens do hub, e o diálogo é o `<dialog>` da casa.

/** O tom → as classes, num lugar só. "Neutro" é contorno, e não fundo: o `bg-tile` do hub é alaranjado. */
const CLASSE_DO_TOM: Record<Tom, string> = {
  neutro: "border border-borda text-cinza",
  ok: "bg-aviso-fundo text-aviso-tinta",
  atencao: "bg-alerta-fundo text-alerta-tinta",
  erro: "bg-erro-fundo text-erro-tinta",
  info: "bg-chip text-chip-tinta",
};

export function Chip({ tom, children, title }: { tom: Tom; children: ReactNode; title?: string }) {
  return (
    <span title={title} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${CLASSE_DO_TOM[tom]}`} data-tom={tom}>
      {children}
    </span>
  );
}

export function Rotulo({ children }: { children: ReactNode }) {
  return <span className="text-[11px] font-bold tracking-[0.12em] text-cinza uppercase">{children}</span>;
}

// ── Ícones (SVG próprios, como os do hub: o hub não tem biblioteca de ícones) ──────────────────────────────
const base = (cls: string) => ({ className: cls, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true });
export const IconeEstrela = ({ cheia = false, className = "size-4" }: { cheia?: boolean; className?: string }) => (
  <svg {...base(className)} fill={cheia ? "currentColor" : "none"}><path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z" /></svg>
);
export const IconeBusca = ({ className = "size-4" }: { className?: string }) => (
  <svg {...base(className)}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
);
export const IconeMais = ({ className = "size-4" }: { className?: string }) => (
  <svg {...base(className)}><path d="M12 5v14M5 12h14" /></svg>
);
export const IconeMarcador = ({ className = "size-4" }: { className?: string }) => (
  <svg {...base(className)}><path d="M6 3h12v18l-6-4-6 4z" /></svg>
);
export const IconeAlca = ({ className = "size-4" }: { className?: string }) => (
  <svg {...base(className)}><circle cx="9" cy="6" r="1" /><circle cx="15" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="9" cy="18" r="1" /><circle cx="15" cy="18" r="1" /></svg>
);
export const IconeCheck = ({ className = "size-4" }: { className?: string }) => (
  <svg {...base(className)}><path d="m5 12 5 5 9-10" /></svg>
);
export const IconeEnviar = ({ className = "size-4" }: { className?: string }) => (
  <svg {...base(className)}><path d="M12 15V4M7 9l5-5 5 5" /><path d="M5 15v4a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-4" /></svg>
);
export const IconeDocumento = ({ className = "size-4" }: { className?: string }) => (
  <svg {...base(className)}><path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7z" /><path d="M14 3v4h4M9 13h6M9 17h4" /></svg>
);
export const IconeBaixar = ({ className = "size-4" }: { className?: string }) => (
  <svg {...base(className)}><path d="M12 4v11M7 10l5 5 5-5" /><path d="M5 19h14" /></svg>
);
export const IconeAbrirFora = ({ className = "size-4" }: { className?: string }) => (
  <svg {...base(className)}><path d="M14 4h6v6M20 4l-9 9" /><path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></svg>
);
export const IconeLixo = ({ className = "size-4" }: { className?: string }) => (
  <svg {...base(className)}><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" /></svg>
);
export const IconeTexto = ({ className = "size-4" }: { className?: string }) => (
  <svg {...base(className)}><path d="M5 6h14M5 10h14M5 14h10M5 18h7" /></svg>
);

/** Um controle segmentado: uma escolha entre poucas, com `aria-pressed`. */
export function Alternador<T extends string>({ rotulo, opcoes, valor, aoMudar }: {
  rotulo: string; opcoes: readonly { id: T; rotulo: string }[]; valor: T; aoMudar: (v: T) => void;
}) {
  return (
    <div role="group" aria-label={rotulo} className="inline-flex rounded-full border border-borda p-0.5">
      {opcoes.map((o) => (
        <button key={o.id} type="button" aria-pressed={valor === o.id} onClick={() => aoMudar(o.id)}
          className={`min-h-9 rounded-full px-3.5 text-sm font-semibold transition ${valor === o.id ? "bg-acento text-acento-tinta" : "text-cinza hover:text-titulo"}`}
          data-alternador={o.id}>
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}

/** A etapa como BARRA SEGMENTADA — um bloco por etapa do funil de 10: "etapa 7 de 10, Laudo" diz onde o processo está. */
export function EtapaProgresso({ pericia, sobreNavy = false, grande = false }: { pericia: Pericia; sobreNavy?: boolean; grande?: boolean }) {
  const etapa = etapaNumDe(pericia);
  return (
    <div data-etapa={etapa}>
      <div className={`flex items-baseline justify-between gap-2 ${grande ? "text-sm" : "text-xs"}`}>
        <span className={`truncate font-semibold ${sobreNavy ? "text-white" : "text-titulo"}`}>{ETAPA_LABEL[etapa]}</span>
        <span className={`shrink-0 font-semibold tabular-nums ${sobreNavy ? "text-navy-mute" : "text-cinza"}`}>Etapa {etapa} de {TOTAL_ETAPAS}</span>
      </div>
      <div className={grande ? "mt-2" : "mt-1.5"}>
        <Segmentos total={TOTAL_ETAPAS} acesos={etapa} rotulo={`Etapa ${ETAPA_LABEL[etapa]}`} sobreNavy={sobreNavy} />
      </div>
    </div>
  );
}

/** A próxima ação de uma perícia, editável no lugar, com o prazo mais próximo ESCRITO ("Vence hoje"). */
export function ProximaAcaoInline({ pericia }: { pericia: Pericia }) {
  const { prazos, updatePericia, podeEscrever } = usePerito();
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(pericia.proximaAcao ?? "");
  const rel = prazoRelacionado(pericia, prazos);
  const dias = rel ? rel.dias : null;
  const salvar = () => {
    updatePericia(pericia.id, { proximaAcao: texto.trim() || undefined });
    setEditando(false);
  };
  const cancelar = () => {
    setTexto(pericia.proximaAcao ?? "");
    setEditando(false);
  };
  return (
    <div className="relative z-10 rounded-xl border border-borda bg-fundo px-3 py-2.5" data-proxima-acao>
      <div className="flex items-center justify-between gap-2">
        <Rotulo>Próxima ação</Rotulo>
        <Chip tom={tomDosDias(dias)} title={rel ? `Prazo relacionado: ${formatarData(rel.data)}` : "Sem prazo relacionado"}>{rotuloDias(dias)}</Chip>
      </div>
      {editando ? (
        <div className="mt-2 flex items-center gap-1">
          <input className={`${CAMPO} py-1.5 text-sm`} value={texto} autoFocus maxLength={500} placeholder="Ex.: Cobrar documentos pendentes"
            onChange={(e) => setTexto(e.target.value)} aria-label="Próxima ação"
            onKeyDown={(e) => { if (e.key === "Enter") salvar(); if (e.key === "Escape") cancelar(); }} />
          <button type="button" onClick={salvar} aria-label="Salvar a próxima ação" className="grid size-9 shrink-0 place-items-center rounded-full text-aviso-tinta hover:bg-chip"><IconeCheck /></button>
          <button type="button" onClick={cancelar} aria-label="Cancelar" className="grid size-9 shrink-0 place-items-center rounded-full text-cinza hover:bg-chip">×</button>
        </div>
      ) : podeEscrever ? (
        <button type="button" onClick={() => setEditando(true)} className="mt-1.5 block w-full text-left text-sm leading-snug">
          <span className={pericia.proximaAcao ? "text-tinta" : "text-cinza italic"}>{pericia.proximaAcao || "Definir a próxima ação"}</span>
        </button>
      ) : (
        <p className="mt-1.5 text-sm leading-snug text-tinta">{pericia.proximaAcao || <span className="text-cinza italic">Sem próxima ação</span>}</p>
      )}
    </div>
  );
}

/**
 * O diálogo da casa: `<dialog>` nativo, sempre centrado (`m-auto`: o reset do hub zera a margem, e sem ela o
 * diálogo abre no canto — a cicatriz do Mapa de Concursos), com a rolagem só no meio.
 */
export function Dialogo({ aberto, aoFechar, titulo, children, rodape, largo = false }: {
  aberto: boolean; aoFechar: () => void; titulo: string; children: ReactNode; rodape?: ReactNode; largo?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (aberto && !d.open) d.showModal();
    if (!aberto && d.open) d.close();
  }, [aberto]);
  return (
    <dialog ref={ref} onClose={aoFechar} onCancel={aoFechar}
      className={`m-auto max-h-[90dvh] w-[calc(100%-2rem)] ${largo ? "max-w-2xl" : "max-w-lg"} rounded-cf-lg border border-borda bg-superficie p-0 text-tinta backdrop:bg-black/50`}
      aria-labelledby="perito-dialogo-titulo">
      {aberto && (
        <div className="flex max-h-[90dvh] flex-col">
          <header className="flex items-center justify-between gap-3 border-b border-borda px-5 py-4">
            <h2 id="perito-dialogo-titulo" className="font-display text-lg font-bold text-titulo">{titulo}</h2>
            <button type="button" onClick={aoFechar} aria-label="Fechar" className="grid size-9 place-items-center rounded-full text-cinza hover:bg-chip">×</button>
          </header>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {rodape && <footer className="flex flex-wrap justify-end gap-2 border-t border-borda px-5 py-3">{rodape}</footer>}
        </div>
      )}
    </dialog>
  );
}

/** Um campo com o rótulo em cima. */
export function Campo({ rotulo, children, dica }: { rotulo: string; children: ReactNode; dica?: string }) {
  return (
    <label className="flex flex-col gap-1 text-sm text-cinza">
      <span className="font-semibold text-tinta">{rotulo}</span>
      {children}
      {dica && <span className="text-xs">{dica}</span>}
    </label>
  );
}

export const BOTAO_SECUNDARIO =
  "inline-flex min-h-10 items-center gap-1.5 rounded-full border border-borda px-4 text-sm font-semibold text-tinta transition hover:border-acento hover:text-titulo disabled:opacity-55";
export const BOTAO_PERIGO =
  "inline-flex min-h-10 items-center gap-1.5 rounded-full border border-erro-borda px-4 text-sm font-semibold text-erro-tinta transition hover:bg-erro-fundo disabled:opacity-55";

// ── A navegação e os alertas (fase B) ──────────────────────────────────────────────────────────────────────

/** As seções da Central, na ordem da barra de lá. O id é o primeiro trecho do endereço depois do slug. */
export const SECOES_DA_CENTRAL = [
  { id: "", rotulo: "Painel" },
  { id: "pericias", rotulo: "Perícias" },
  { id: "prazos", rotulo: "Prazos" },
  { id: "tarefas", rotulo: "Tarefas" },
  { id: "financeiro", rotulo: "Financeiro" },
  { id: "relatorios", rotulo: "Relatórios" },
  { id: "inteligencia", rotulo: "Inteligência" },
  { id: "alertas", rotulo: "Alertas" },
  { id: "meus-dados", rotulo: "Meus dados" },
] as const;

export type SecaoDaCentral = (typeof SECOES_DA_CENTRAL)[number]["id"];

/**
 * A barra das seções. Lá era uma barra lateral; aqui é uma linha de pílulas, que no celular rola de lado, porque
 * a ferramenta mora dentro da página do hub, com o Topo dele em cima. Os Alertas levam o número dos que pedem
 * atenção (como a barra de lá): é o único número da barra, e some no zero.
 */
export function NavDaCentral({ raiz, ativa, pedemAtencao }: { raiz: string; ativa: SecaoDaCentral; pedemAtencao: number }) {
  return (
    <nav aria-label="Seções da Central do Perito" className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0" data-nav-da-central>
      <ul className="flex w-max gap-1.5 sm:w-auto sm:flex-wrap">
        {SECOES_DA_CENTRAL.map((s) => {
          const atual = s.id === ativa;
          return (
            <li key={s.id || "painel"}>
              <Link to={s.id ? `${raiz}/${s.id}` : raiz} aria-current={atual ? "page" : undefined} data-secao={s.id || "painel"}
                className={`inline-flex min-h-10 items-center gap-2 rounded-full border px-4 text-sm font-semibold whitespace-nowrap transition ${
                  atual ? "border-acento bg-acento text-acento-tinta" : "border-borda bg-superficie text-cinza hover:border-acento hover:text-titulo"
                }`}>
                {s.rotulo}
                {s.id === "alertas" && pedemAtencao > 0 && (
                  <span className={`rounded-full px-1.5 text-xs tabular-nums ${atual ? "bg-white/20" : "bg-erro-fundo text-erro-tinta"}`}
                    aria-label={`${pedemAtencao} ${pedemAtencao === 1 ? "pede" : "pedem"} atenção`} data-contagem-de-alertas>
                    {pedemAtencao}
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

const NIVEL_DO_ALERTA: Record<NivelAlerta, { rotulo: string; caixa: string }> = {
  critico: { rotulo: "Crítico", caixa: "border-erro-borda bg-erro-fundo text-erro-tinta" },
  atencao: { rotulo: "Atenção", caixa: "border-alerta-tinta/30 bg-alerta-fundo text-alerta-tinta" },
  info: { rotulo: "Informação", caixa: "border-borda bg-chip text-chip-tinta" },
  sucesso: { rotulo: "Em dia", caixa: "border-aviso-borda bg-aviso-fundo text-aviso-tinta" },
};

/**
 * Os alertas em lista, do mais antigo ao mais novo, cada um levando à perícia dele. O nível é dito em PALAVRA,
 * além da cor (lá era um ícone com `aria-label`, que quem não distingue as cores não lia na tela).
 */
export function FeedDeAlertas({ raiz, alertas, limite }: { raiz: string; alertas: Alerta[]; limite?: number }) {
  const lista = limite ? alertas.slice(0, limite) : alertas;
  if (lista.length === 0) {
    return (
      <p className="rounded-xl bg-aviso-fundo p-4 text-sm text-aviso-tinta" data-sem-alertas>
        <strong className="font-semibold">Nenhum alerta agora.</strong> Prazos, tarefas e honorários estão em dia.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-2" data-feed-de-alertas>
      {lista.map((a) => {
        const n = NIVEL_DO_ALERTA[a.nivel];
        const conteudo = (
          <>
            <span className="text-[11px] font-bold tracking-[0.12em] uppercase">{n.rotulo}</span>
            <span className="block text-sm font-semibold">{a.titulo}</span>
            {a.detalhe && <span className="block text-xs">{a.detalhe}</span>}
            <span className="mt-0.5 block text-xs tabular-nums">{formatarData(a.dia)}</span>
          </>
        );
        return (
          <li key={a.chave} data-alerta={a.chave} data-nivel={a.nivel}>
            {a.pericia_id ? (
              <Link to={`${raiz}/pericias/${a.pericia_id}`} className={`block rounded-xl border px-3.5 py-3 transition hover:-translate-y-px ${n.caixa}`}>{conteudo}</Link>
            ) : (
              <div className={`rounded-xl border px-3.5 py-3 ${n.caixa}`}>{conteudo}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Um bloco de tela: o título, um atalho opcional à direita e o conteúdo, sobre a superfície. */
export function Secao({ titulo, descricao, acao, children, nome }: { titulo: string; descricao?: string; acao?: ReactNode; children: ReactNode; nome: string }) {
  return (
    <section className="surface rise p-5 sm:p-6" data-bloco={nome}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-lg font-bold text-titulo">{titulo}</h2>
          {descricao && <p className="mt-0.5 text-sm text-cinza">{descricao}</p>}
        </div>
        {acao}
      </div>
      {children}
    </section>
  );
}

/** O atalho do canto de um bloco ("Ver a agenda →"). */
export function Atalho({ para, children }: { para: string; children: ReactNode }) {
  return (
    <Link to={para} className="inline-flex items-center gap-1 text-sm font-semibold text-link hover:underline">
      {children} <span aria-hidden="true" className="text-laranja-texto">→</span>
    </Link>
  );
}
